import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
  Slot,
} from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { User } from "@/models/users/User";
import { X, UserPlus, UserX } from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  TooltipContent,
} from "@capacit-com/lara-law-shared-ui";
import {
  dialogHeaderStyle,
  dialogContentStyle,
  dialogDescriptionStyle,
  dialogDescriptionWrapperStyle,
  userListParentWrapperStyle,
  userListCardWrapperLeft,
  userListCardWrapperRight,
  userListCardStyle,
  userListCardHeaderStyle,
  userListCardHeaderTitleTextStyle,
  userListCardHeaderTitleTooltipStyle,
  inputStyle,
  userListCardHeaderAbsoluteButtonWrapperStyle,
  userListCardHeaderAbsoluteButtonStyle,
  userListComponentStyle,
  userListComponentEmptyStyle,
  dialogFooterStyle,
  dialogFooterButtonStyle,
} from "../utils/constants";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import UserList from "./UserList";
import useUsers from "@/hooks/useUsers";
import useGroups from "@/hooks/useGroups";
import { Group } from "@/models/feature/feature";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
interface CreateAndEditGroupDialogProps {
  trigger?: React.ReactNode;
  validateGroupName: (name: string, groupId?: string) => boolean;
  group?: Group;
  /** When provided, dialog is controlled (use for opening programmatically e.g. from table row) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}
const CreateAndEditGroupDialog: FC<CreateAndEditGroupDialogProps> = ({
  trigger,
  validateGroupName,
  group,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  // Hooks
  const { useQueryAllTenantUsers } = useUsers();
  const { create, update } = useGroups();

  // State (uncontrolled when open/onOpenChange not provided)
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOnOpenChange
    ? (controlledOpen ?? false)
    : internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;
  const [groupName, setGroupName] = useState<string>("");
  const [searchDeselectedUsers, setSearchDeselectedUsers] =
    useState<string>("");
  const [searchSelectedUsers, setSearchSelectedUsers] = useState<string>("");
  const [deselectedUsersFiltered, setDeselectedUsersFiltered] = useState<
    User[]
  >([]);
  const [selectedUsersFiltered, setSelectedUsersFiltered] = useState<User[]>(
    []
  );
  const [deselectedUsers, setDeselectedUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchSelectedInputRef = useRef<HTMLInputElement>(null);

  // Queries
  const fetchedUsers = useQueryAllTenantUsers();

  // Handlers
  const handleAddUser = (userId: string) => {
    const userToAdd = deselectedUsers.find((user) => user.id === userId);
    if (userToAdd) {
      setSelectedUsers([...selectedUsers, userToAdd]);
      setDeselectedUsers(deselectedUsers.filter((user) => user.id !== userId));
    }
  };

  const handleAddAllUsers = () => {
    setSelectedUsers([...selectedUsers, ...deselectedUsers]);
    setDeselectedUsers([]);
  };

  const handleRemoveAllUsers = () => {
    setDeselectedUsers([...deselectedUsers, ...selectedUsers]);
    setSelectedUsers([]);
  };

  const handleRemoveUser = (userId: string) => {
    const userToRemove = selectedUsers.find((user) => user.id === userId);
    if (userToRemove) {
      setDeselectedUsers([...deselectedUsers, userToRemove]);
      setSelectedUsers(selectedUsers.filter((user) => user.id !== userId));
    }
  };

  const handleCreateGroup = async () => {
    try {
      if (!validateGroupName(groupName)) {
        return;
      }

      await create.mutateAsync({
        name: groupName,
        userIds: selectedUsers.map((user) => user.id),
      });

      setGroupName("");
      setSelectedUsers([]);
      setDeselectedUsers([]);
      setSelectedUsersFiltered([]);
      setDeselectedUsersFiltered([]);

      setOpen(false);
      showSuccessToast("Gruppen blev oprettet.");
    } catch (error) {
      logger.error("Failed to create group", {
        operation: "createGroup",
        error,
        userCount: selectedUsers.length,
      });
      showErrorToast("Gruppen kunne ikke oprettes. Prøv igen.");
    }
  };

  const handledEditGroup = async () => {
    try {
      if (!validateGroupName(groupName, group?.id)) {
        return;
      }
      if (!group) {
        logger.warn("Edit group dialog has no group to update", {
          operation: "updateGroup",
        });
        showErrorToast("Gruppen kunne ikke opdateres. Prøv igen.");
        return;
      }

      await update.mutateAsync({
        id: group?.id,
        name: groupName,
        userIds: selectedUsers.map((user) => user.id),
      });

      setGroupName("");
      setSelectedUsers([]);
      setDeselectedUsers([]);
      setSelectedUsersFiltered([]);
      setDeselectedUsersFiltered([]);

      setOpen(false);
      showSuccessToast("Gruppen blev opdateret.");
    } catch (error) {
      logger.error("Failed to update group", {
        operation: "updateGroup",
        error,
        groupId: group?.id,
        userCount: selectedUsers.length,
      });
      showErrorToast("Gruppen kunne ikke opdateres. Prøv igen.");
    }
  };

  // What the discard guard protects: the name and the member list, compared
  // with the group as it was opened (an empty group for "Opret ny gruppe").
  const initialUserIds = useMemo(
    () => [...(group?.userIds ?? [])].sort().join(","),
    [group]
  );
  const selectedUserIds = selectedUsers
    .map((user) => user.id)
    .sort()
    .join(",");
  const isDirty =
    groupName !== (group?.name ?? "") || selectedUserIds !== initialUserIds;

  // Effect: seed from the group every time the dialog opens, so a discarded
  // edit does not linger into the next open.
  useEffect(() => {
    if (!open) return;
    setSearchDeselectedUsers("");
    setSearchSelectedUsers("");
    if (fetchedUsers.isFetched && fetchedUsers.data && !group) {
      setGroupName("");
      setSelectedUsers([]);
      setSelectedUsersFiltered([]);
      setDeselectedUsers(fetchedUsers.data);
      setDeselectedUsersFiltered(fetchedUsers.data);
    }
    if (fetchedUsers.isFetched && fetchedUsers.data && group) {
      setGroupName(group.name);
      setSelectedUsers(
        fetchedUsers.data.filter((user) => group.userIds.includes(user.id))
      );
      setSelectedUsersFiltered(
        fetchedUsers.data.filter((user) => group.userIds.includes(user.id))
      );
      setDeselectedUsers(
        fetchedUsers.data.filter((user) => !group.userIds.includes(user.id))
      );
      setDeselectedUsersFiltered(
        fetchedUsers.data.filter((user) => !group.userIds.includes(user.id))
      );
    }
  }, [open, fetchedUsers.data, fetchedUsers.isFetched, group]);

  const userMatchesSearch = (user: User, search: string) => {
    const term = search.toLowerCase().trim();
    if (!term) return true;
    const name = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const email = (user.email ?? "").toLowerCase();
    return name.includes(term) || email.includes(term);
  };

  useEffect(() => {
    if (searchDeselectedUsers.length > 0) {
      setDeselectedUsersFiltered(
        deselectedUsers.filter((user) =>
          userMatchesSearch(user, searchDeselectedUsers)
        )
      );
    } else {
      setDeselectedUsersFiltered(deselectedUsers);
    }
  }, [searchDeselectedUsers, deselectedUsers]);

  useEffect(() => {
    if (searchSelectedUsers.length > 0) {
      setSelectedUsersFiltered(
        selectedUsers.filter((user) =>
          userMatchesSearch(user, searchSelectedUsers)
        )
      );
    } else {
      setSelectedUsersFiltered(selectedUsers);
    }
  }, [searchSelectedUsers, selectedUsers]);

  return (
    <>
      {trigger != null && <Slot onClick={() => setOpen(true)}>{trigger}</Slot>}
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        isDirty={isDirty}
        size="xl"
        className={dialogContentStyle}
      >
        <DialogHeader className={dialogHeaderStyle}>
          <DialogTitle>
            {group ? "Rediger gruppe" : "Opret ny gruppe"}
          </DialogTitle>
          <div className="flex flex-col gap-2 pt-2">
            <p className="text-sm font-medium">Gruppenavn</p>
            <Input
              type="text"
              placeholder="Navn"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </div>
        </DialogHeader>

        <DialogDescription asChild className={dialogDescriptionStyle}>
          <div className={dialogDescriptionWrapperStyle}>
            <TooltipProvider>
              <div className={userListParentWrapperStyle}>
                <div className={userListCardWrapperLeft}>
                  <div className={userListCardStyle}>
                    <div className={userListCardHeaderStyle}>
                      <p className={userListCardHeaderTitleTextStyle}>
                        Alle brugere
                      </p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mr-2"
                            onClick={handleAddAllUsers}
                          >
                            <UserPlus className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className={userListCardHeaderTitleTooltipStyle}>
                            Tilføj alle brugere til gruppen
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <Input
                        type="text"
                        placeholder="Søg"
                        className={inputStyle}
                        onChange={(e) =>
                          setSearchDeselectedUsers(e.target.value)
                        }
                        value={searchDeselectedUsers}
                        ref={searchInputRef}
                      />
                      <div
                        className={userListCardHeaderAbsoluteButtonWrapperStyle}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (searchDeselectedUsers.length > 0) {
                              setSearchDeselectedUsers("");
                            } else {
                              // Focus the input when clicking the button with empty input
                              searchInputRef.current?.focus();
                            }
                          }}
                          className={`${userListCardHeaderAbsoluteButtonStyle} ${
                            searchDeselectedUsers.length > 0
                              ? "opacity-50"
                              : "opacity-0 hover:opacity-0 cursor-text"
                          }`}
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    </div>
                    {deselectedUsersFiltered.length > 0 && (
                      <UserList
                        users={deselectedUsersFiltered}
                        onAddUser={handleAddUser}
                        className={userListComponentStyle}
                      />
                    )}
                    {deselectedUsersFiltered.length === 0 && (
                      <p className={userListComponentEmptyStyle}>
                        Ingen brugere fundet
                      </p>
                    )}
                  </div>
                </div>
                <div className={userListCardWrapperRight}>
                  <div className={userListCardStyle}>
                    <div className={userListCardHeaderStyle}>
                      <p className={userListCardHeaderTitleTextStyle}>
                        Valgte brugere
                      </p>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mr-2"
                            onClick={handleRemoveAllUsers}
                          >
                            <UserX className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className={userListCardHeaderTitleTooltipStyle}>
                            Fjern alle brugere fra gruppen
                          </p>
                        </TooltipContent>
                      </Tooltip>

                      <Input
                        type="text"
                        placeholder="Søg"
                        className={inputStyle}
                        onChange={(e) => setSearchSelectedUsers(e.target.value)}
                        value={searchSelectedUsers}
                        ref={searchSelectedInputRef}
                      />
                      <div
                        className={userListCardHeaderAbsoluteButtonWrapperStyle}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (searchSelectedUsers.length > 0) {
                              setSearchSelectedUsers("");
                            } else {
                              // Focus the input when clicking the button with empty input
                              searchSelectedInputRef.current?.focus();
                            }
                          }}
                          className={`${userListCardHeaderAbsoluteButtonStyle} ${
                            searchSelectedUsers.length > 0
                              ? "opacity-50"
                              : "opacity-0 hover:opacity-0 cursor-text"
                          }`}
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    </div>
                    {selectedUsersFiltered.length > 0 && (
                      <UserList
                        users={selectedUsersFiltered}
                        onRemoveUser={handleRemoveUser}
                        className={userListComponentStyle}
                      />
                    )}
                    {selectedUsersFiltered.length === 0 &&
                      searchSelectedUsers.length === 0 && (
                        <p className={userListComponentEmptyStyle}>
                          Ingen brugere valgt
                        </p>
                      )}
                    {selectedUsersFiltered.length === 0 &&
                      searchSelectedUsers.length > 0 && (
                        <p className={userListComponentEmptyStyle}>
                          Ingen brugere fundet
                        </p>
                      )}
                  </div>
                </div>
              </div>
            </TooltipProvider>
          </div>
        </DialogDescription>
        <DialogFooter className={dialogFooterStyle}>
          <FormDialogCancel disabled={create.isPending || update.isPending} />
          <Button
            variant="default"
            disabled={!groupName || create.isPending || update.isPending}
            onClick={group ? handledEditGroup : handleCreateGroup}
            className={dialogFooterButtonStyle}
          >
            {create.isPending || update.isPending ? (
              <Spinner className="text-primary-foreground" />
            ) : group ? (
              "Gem"
            ) : (
              "Opret"
            )}
          </Button>
        </DialogFooter>
      </FormDialog>
    </>
  );
};

export default CreateAndEditGroupDialog;
